import { useState, useEffect, useRef } from "react";
import { apiClient } from "@/App";
import { toast } from "sonner";
import { 
  FileText, Upload, Trash2, X, Loader2, ExternalLink, 
  FolderOpen, FileCheck, FilePen, FileSpreadsheet, FileImage,
  AlertCircle, Link2, File
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const documentTypes = [
  { value: "contract", label: "Contract", icon: FileCheck, color: "text-green-500" },
  { value: "drawing", label: "Drawing", icon: FileImage, color: "text-blue-500" },
  { value: "approval", label: "Approval", icon: FileCheck, color: "text-purple-500" },
  { value: "invoice", label: "Invoice", icon: FileSpreadsheet, color: "text-yellow-500" },
  { value: "report", label: "Report", icon: FilePen, color: "text-orange-500" },
  { value: "other", label: "Other", icon: FileText, color: "text-gray-500" }
];

const getDocIcon = (type) => {
  const docType = documentTypes.find(t => t.value === type);
  return docType || documentTypes.find(t => t.value === "other");
};

export default function DocumentVault({ projectId }) {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [filter, setFilter] = useState("all");
  const [uploadMode, setUploadMode] = useState("file"); // "file" or "url"
  const [cloudinaryConfigured, setCloudinaryConfigured] = useState(false);
  const fileInputRef = useRef(null);
  const [newDoc, setNewDoc] = useState({
    document_url: "",
    document_name: "",
    document_type: "other",
    description: "",
    file: null
  });

  useEffect(() => {
    fetchDocuments();
    checkCloudinaryStatus();
  }, [projectId, filter]);

  const checkCloudinaryStatus = async () => {
    try {
      const response = await apiClient.get("/cloudinary/status");
      setCloudinaryConfigured(response.data.configured);
    } catch (error) {
      setCloudinaryConfigured(false);
    }
  };

  const fetchDocuments = async () => {
    try {
      const url = filter === "all"
        ? `/projects/${projectId}/documents`
        : `/projects/${projectId}/documents?document_type=${filter}`;
      const response = await apiClient.get(url);
      setDocuments(response.data);
    } catch (error) {
      console.error("Error fetching documents:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file size (max 20MB)
      if (file.size > 20 * 1024 * 1024) {
        toast.error("File size must be less than 20MB");
        return;
      }
      // Auto-fill document name from filename
      const nameWithoutExt = file.name.replace(/\.[^/.]+$/, "");
      setNewDoc({ 
        ...newDoc, 
        file,
        document_name: newDoc.document_name || nameWithoutExt
      });
    }
  };

  const handleUpload = async () => {
    if (!newDoc.document_name) {
      toast.error("Please enter a document name");
      return;
    }
    
    if (uploadMode === "file") {
      if (!newDoc.file) {
        toast.error("Please select a file to upload");
        return;
      }
      if (!cloudinaryConfigured) {
        toast.error("File upload is not configured. Please use URL mode or contact admin.");
        return;
      }
    } else {
      if (!newDoc.document_url) {
        toast.error("Please enter a document URL");
        return;
      }
    }
    
    setUploading(true);
    try {
      if (uploadMode === "file") {
        // Upload file
        const formData = new FormData();
        formData.append("file", newDoc.file);
        formData.append("project_id", projectId);
        formData.append("document_name", newDoc.document_name);
        formData.append("document_type", newDoc.document_type);
        formData.append("description", newDoc.description || "");
        
        await apiClient.post("/upload/document", formData, {
          headers: { "Content-Type": "multipart/form-data" }
        });
        toast.success("Document uploaded successfully");
      } else {
        // Upload via URL
        await apiClient.post(`/projects/${projectId}/documents`, {
          project_id: projectId,
          document_url: newDoc.document_url,
          document_name: newDoc.document_name,
          document_type: newDoc.document_type,
          description: newDoc.description
        });
        toast.success("Document added successfully");
      }
      
      setNewDoc({ document_url: "", document_name: "", document_type: "other", description: "", file: null });
      if (fileInputRef.current) fileInputRef.current.value = "";
      setShowUpload(false);
      fetchDocuments();
    } catch (error) {
      console.error("Upload error:", error);
      toast.error(error.response?.data?.detail || "Failed to upload document");
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (docId) => {
    if (!confirm("Delete this document?")) return;
    try {
      await apiClient.delete(`/projects/${projectId}/documents/${docId}`);
      toast.success("Document deleted");
      fetchDocuments();
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  // Group documents by type
  const groupedDocs = documents.reduce((acc, doc) => {
    const type = doc.document_type || "other";
    if (!acc[type]) acc[type] = [];
    acc[type].push(doc);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="document-vault">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-[150px] bg-[#1A1A1A] border-[#262626] text-sm">
              <SelectValue placeholder="Filter" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Documents</SelectItem>
              {documentTypes.map(type => (
                <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <span className="text-xs text-gray-500">{documents.length} documents</span>
        </div>
        <Button 
          size="sm" 
          className="bg-[#FF4500] hover:bg-[#E65100]"
          onClick={() => setShowUpload(true)}
          data-testid="add-document-btn"
        >
          <Upload className="w-4 h-4 mr-1" />
          Add Document
        </Button>
      </div>

      {/* Documents List */}
      {documents.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-gray-500">
          <FolderOpen className="w-12 h-12 mb-3 opacity-50" />
          <p className="text-sm">No documents yet</p>
          <p className="text-xs mt-1">Store project contracts, drawings & approvals</p>
        </div>
      ) : (
        <div className="space-y-6">
          {Object.entries(groupedDocs).map(([type, docs]) => {
            const typeInfo = getDocIcon(type);
            const Icon = typeInfo.icon;
            
            return (
              <div key={type}>
                <h4 className="text-sm font-medium text-gray-400 mb-3 flex items-center gap-2">
                  <Icon className={`w-4 h-4 ${typeInfo.color}`} />
                  {typeInfo.label}
                  <span className="text-xs text-gray-600">({docs.length})</span>
                </h4>
                <div className="space-y-2">
                  {docs.map((doc) => (
                    <div 
                      key={doc.document_id}
                      className="bg-[#1A1A1A] border border-[#262626] rounded-lg p-4 flex items-center justify-between group hover:border-[#404040] transition-colors"
                    >
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div className={`w-10 h-10 rounded-lg bg-[#262626] flex items-center justify-center ${typeInfo.color}`}>
                          <Icon className="w-5 h-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h5 className="text-white font-medium text-sm truncate">{doc.document_name}</h5>
                          {doc.description && (
                            <p className="text-xs text-gray-500 truncate">{doc.description}</p>
                          )}
                          <p className="text-[10px] text-gray-600 mt-1">
                            {new Date(doc.uploaded_at).toLocaleDateString()}
                            {doc.file_size && ` • ${(doc.file_size / 1024 / 1024).toFixed(2)} MB`}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <a 
                          href={doc.document_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-[#FF4500] hover:text-[#FF6B35] text-xs flex items-center gap-1"
                        >
                          <ExternalLink className="w-3 h-3" />
                          Open
                        </a>
                        <button
                          onClick={() => handleDelete(doc.document_id)}
                          className="text-gray-500 hover:text-red-500 p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Upload Modal */}
      {showUpload && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-[#1A1A1A] border border-[#262626] rounded-lg p-6 max-w-md w-full">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">Add Document</h3>
              <button onClick={() => setShowUpload(false)} className="text-gray-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {/* Upload Mode Toggle */}
            <div className="flex gap-2 mb-4">
              <button
                onClick={() => setUploadMode("file")}
                className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-all ${
                  uploadMode === "file" 
                    ? "bg-[#FF4500] text-white" 
                    : "bg-[#262626] text-gray-400 hover:text-white"
                }`}
              >
                <Upload className="w-4 h-4" />
                Upload File
              </button>
              <button
                onClick={() => setUploadMode("url")}
                className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-all ${
                  uploadMode === "url" 
                    ? "bg-[#FF4500] text-white" 
                    : "bg-[#262626] text-gray-400 hover:text-white"
                }`}
              >
                <Link2 className="w-4 h-4" />
                Paste URL
              </button>
            </div>
            
            {/* Cloudinary Warning */}
            {uploadMode === "file" && !cloudinaryConfigured && (
              <div className="mb-4 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                <div className="text-xs text-amber-400">
                  <p className="font-medium">File upload not configured</p>
                  <p className="mt-1 text-amber-500/80">Add Cloudinary credentials in Settings to enable file uploads. Use URL mode for now.</p>
                </div>
              </div>
            )}
            
            <div className="space-y-4">
              {uploadMode === "file" ? (
                <div>
                  <Label className="text-xs text-gray-400">Select File</Label>
                  <input
                    ref={fileInputRef}
                    type="file"
                    onChange={handleFileSelect}
                    className="w-full mt-1 text-sm text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-[#FF4500] file:text-white hover:file:bg-[#E65100] file:cursor-pointer"
                    data-testid="document-file-input"
                  />
                  {newDoc.file && (
                    <p className="text-xs text-green-400 mt-2 flex items-center gap-1">
                      <File className="w-3 h-3" />
                      {newDoc.file.name} ({(newDoc.file.size / 1024 / 1024).toFixed(2)} MB)
                    </p>
                  )}
                </div>
              ) : (
                <div>
                  <Label className="text-xs text-gray-400">Document URL</Label>
                  <Input
                    value={newDoc.document_url}
                    onChange={(e) => setNewDoc({ ...newDoc, document_url: e.target.value })}
                    placeholder="https://drive.google.com/..."
                    className="bg-[#171717] border-[#262626]"
                    data-testid="document-url-input"
                  />
                </div>
              )}
              
              <div>
                <Label className="text-xs text-gray-400">Document Name *</Label>
                <Input
                  value={newDoc.document_name}
                  onChange={(e) => setNewDoc({ ...newDoc, document_name: e.target.value })}
                  placeholder="e.g., Project Contract"
                  className="bg-[#171717] border-[#262626]"
                  data-testid="document-name-input"
                />
              </div>
              
              <div>
                <Label className="text-xs text-gray-400">Type</Label>
                <Select value={newDoc.document_type} onValueChange={(v) => setNewDoc({ ...newDoc, document_type: v })}>
                  <SelectTrigger className="bg-[#171717] border-[#262626]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {documentTypes.map(type => (
                      <SelectItem key={type.value} value={type.value}>
                        <span className="flex items-center gap-2">
                          <type.icon className={`w-3 h-3 ${type.color}`} />
                          {type.label}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label className="text-xs text-gray-400">Description (optional)</Label>
                <Textarea
                  value={newDoc.description}
                  onChange={(e) => setNewDoc({ ...newDoc, description: e.target.value })}
                  placeholder="Brief description..."
                  className="bg-[#171717] border-[#262626] min-h-[60px]"
                />
              </div>
              
              <div className="flex gap-3 pt-2">
                <Button variant="outline" className="flex-1 border-[#262626]" onClick={() => setShowUpload(false)}>
                  Cancel
                </Button>
                <Button 
                  className="flex-1 bg-[#FF4500] hover:bg-[#E65100]" 
                  onClick={handleUpload} 
                  disabled={uploading || (uploadMode === "file" && !cloudinaryConfigured)}
                  data-testid="upload-document-btn"
                >
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Upload"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
